import { AllPermissions, type FeatureCode, type Permission } from '../generated/enums';
import type { Session } from './types';

/**
 * Whether the signed-in user holds a grant *at the branch they are acting in*.
 *
 * Permissions differ per branch — a Branch Manager at head office may hold nothing at the second
 * unit — so this reads the acting branch's list, falling back to the tenant-wide one when no branch
 * is selected. That is the whole reason the menu has to be re-derived on a branch switch rather
 * than computed once at sign-in.
 *
 * `"*"` is the administrator shorthand the API sends in place of hundreds of grants. Special-casing
 * it is not optional: without it, administrators silently lose every module added after their role
 * was seeded.
 */
export function can(session: Session, required: Permission | undefined): boolean {
  if (!required) {
    return true;
  }

  const held = session.branch?.permissions ?? session.user.permissions;

  return held.includes(AllPermissions) || held.includes(required);
}

/**
 * Whether the *customer* bought the capability — a different question from whether this user may
 * use it, and the two are deliberately kept apart.
 */
export function hasFeature(session: Session, feature: FeatureCode | undefined): boolean {
  return !feature || session.user.entitlements.features.includes(feature);
}

/**
 * Whether writes are allowed at all. False once a subscription lapses to read-only, which the UI
 * renders by keeping every module visible and disabling its write actions — mirroring the server
 * rule that treats everything except View and ViewFinancials as a write.
 */
export function canWrite(session: Session): boolean {
  return session.user.entitlements.canWrite;
}

/**
 * Whether a *write* action may be offered: both that this user holds the grant and that the
 * subscription still permits writing.
 *
 * The two are checked together because every write button on every master screen has to ask both
 * questions, and asking only one is the failure that produces a button the API refuses. Reads are
 * deliberately not routed through here — a lapsed tenant keeps `View`, `Export` and `Print`.
 */
export function canAct(session: Session, required: Permission): boolean {
  return can(session, required) && canWrite(session);
}

/**
 * Why a write action is unavailable, as a tooltip — or undefined when it is available.
 *
 * Permission is reported before subscription because it is the more specific answer: a user who
 * holds nothing here would still hold nothing after the invoice is paid, and sending them to
 * whoever pays it wastes both their time.
 *
 * The strings are literals for now; they become resource keys with i18n in Phase 6 (rule 12).
 */
export function blockedReason(session: Session, required: Permission): string | undefined {
  if (!can(session, required)) {
    return 'You do not have permission for this';
  }

  if (!canWrite(session)) {
    return 'Your subscription is read-only';
  }

  return undefined;
}
